import React, { useState, useEffect } from 'react';
import { adminFileAPI } from '../services/api';
import FileViewModal from '../components/FileViewModal';
import ConfirmationModal from '../components/ConfirmationModal';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalFiles: 0,
    pendingFiles: 0,
    approvedFiles: 0,
    rejectedFiles: 0,
  });
  const [recentFiles, setRecentFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  useEffect(() => {
    fetchAdminStats();
  }, []);

  const fetchAdminStats = async () => {
    try {
      const response = await adminFileAPI.getAllSubmittedFiles({ limit: 10 });
      if (response.data.success) {
        const files = response.data.data.files;
        setRecentFiles(files);

        // Calculate stats
        const totalFiles = response.data.data.pagination.totalFiles;
        const pendingFiles = files.filter(f => !f.status || f.status === 'pending').length;
        const approvedFiles = files.filter(f => f.status === 'approved').length;
        const rejectedFiles = files.filter(f => f.status === 'reject').length;

        setStats({
          totalFiles,
          pendingFiles,
          approvedFiles,
          rejectedFiles,
        });
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#28a745';
      case 'reject': return '#dc3545';
      case 'pending': return '#ffc107';
      case 'process': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'reject': return 'Rejected';
      case 'pending': return 'Pending';
      case 'process': return 'Processing';
      default: return 'Unknown';
    }
  };

  const handleViewFile = (file) => {
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
  };

  const handleStatusUpdate = async (fileId, newStatus) => {
    try {
      const response = await adminFileAPI.updateFileStatus({
        _id: fileId,
        status: newStatus,
      });

      if (response.data.success) {
        // Refresh the stats and recent files
        fetchAdminStats();
        return { success: true };
      } else {
        console.error('Status update failed:', response.data.message);
        return { success: false };
      }
    } catch (error) {
      console.error('Error updating status:', error);
      return { success: false };
    }
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-center align-center" style={{ minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ marginBottom: '0.5rem' }}>Admin Dashboard</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Overview of all file submissions and management activities.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4" style={{ marginBottom: '2rem', gap: '1rem' }}>
        <div className="card">
          <div className="card-body">
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: 'var(--orange-dark)',
                marginBottom: '0.5rem',
              }}>
                {stats.totalFiles}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Total Files
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#ffc107',
                marginBottom: '0.5rem',
              }}>
                {stats.pendingFiles}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Pending Review
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#28a745',
                marginBottom: '0.5rem',
              }}>
                {stats.approvedFiles}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Approved
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#dc3545',
                marginBottom: '0.5rem',
              }}>
                {stats.rejectedFiles}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Rejected
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Files */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0 }}>Recent File Submissions</h3>
        </div>
        <div className="card-body">
          {recentFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{
                fontSize: '3rem',
                marginBottom: '1rem',
                opacity: 0.5,
              }}>
                📂
              </div>
              <p style={{ color: 'var(--text-secondary)' }}>No files submitted yet</p>
            </div>
          ) : (
            <div className="file-list">
              {recentFiles.map((file) => (
                <div key={file._id} className="file-item">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                      {file.title || file.original_filename}
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: 'var(--text-secondary)',
                      marginBottom: '0.25rem',
                    }}>
                      {file.description || 'No description'}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                      marginBottom: '0.25rem',
                    }}>
                      Submitted by: {file.user_id?.name || file.user_id?.email} • {formatDate(file.created_date)}
                    </div>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.125rem 0.25rem',
                      borderRadius: 'var(--border-radius-sm)',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      backgroundColor: getStatusColor(file.status),
                      color: 'var(--white)',
                    }}>
                      {getStatusText(file.status)}
                    </div>
                  </div>
                  <div className="d-flex align-center" style={{ gap: '0.5rem' }}>
                    <button
                      onClick={() => handleViewFile(file)}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    >
                      View
                    </button>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={file.status || 'pending'}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          const currentStatusText = getStatusText(file.status);
                          const newStatusText = getStatusText(newStatus);

                          setConfirmationModal({
                            isOpen: true,
                            title: 'Confirm Status Change',
                            message: `Are you sure you want to change the status from "${currentStatusText}" to "${newStatusText}"?\n\nThis action cannot be undone.`,
                            onConfirm: () => {
                              handleStatusUpdate(file._id, newStatus);
                            },
                          });

                          // Reset the select to current value initially
                          e.target.value = file.status || 'pending';
                        }}
                        disabled={(file.status !== 'pending' && file.status !== 'process')}
                        style={{
                          padding: '0.25rem',
                          borderRadius: 'var(--border-radius-sm)',
                          border: '1px solid var(--border-color)',
                          fontSize: '0.75rem',
                          opacity: (file.status === 'pending' || file.status === 'process') ? 1 : 0.6,
                        }}
                      >
                        <option value="pending">Pending</option>
                        <option value="process">Processing</option>
                        <option value="approved">Approved</option>
                        <option value="reject">Rejected</option>
                      </select>
                      {file.status !== 'pending' && file.status !== 'process' && (
                        <span
                          title="Status can only be updated when current status is Pending or Processing"
                          style={{
                            position: 'absolute',
                            right: '-15px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '0.75rem',
                            color: '#ffc107',
                            cursor: 'help',
                          }}
                        >
                          ℹ️
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* File View Modal */}
      <FileViewModal
        file={selectedFile}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onStatusUpdate={handleStatusUpdate}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
      />
    </div>
  );
};

export default AdminDashboard;
